import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import type { Filter, FilterDataMultiSelect, FiltersGroup } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import { optionCheckboxId } from '@/app/common/filters/types/multiSelect/FilterMultiSelect.tsx';
import { ShowMoreButton } from '@/app/components/search/components/ShowMoreContainer/styled.ts';
import { filterGroupTitleId } from '@/app/common/filters/surfaces/forms/groupHeading.ts';

interface ShowMoreControlProps {
  hasLimit: boolean;
  limit?: number;
  onShowMore?: () => void;
  children: (filtersGroup: FiltersGroup) => React.ReactNode;
  filtersGroup: FiltersGroup;
}

export const getSlicedFiltersGroup = (filtersGroup: FiltersGroup, limit: number): FiltersGroup => {
  let remaining = limit;
  const slicedFilters = filtersGroup.filters.reduce<Filter[]>((acc, filter) => {
    if (remaining <= 0) return acc;

    const data = filter.data as FilterDataMultiSelect;
    if (filter.type === FILTER_TYPE.MULTI_SELECT && data?.options) {
      const slicedOptions = data.options.slice(0, remaining);
      remaining -= slicedOptions.length;
      acc.push({ ...filter, data: { ...data, options: slicedOptions } });
    } else {
      // Non-multi-select filters (switches, ranges, etc.) are always included
      acc.push(filter);
    }

    return acc;
  }, []);

  return { ...filtersGroup, filters: slicedFilters };
};

export const revealedOptionIds = (filtersGroup: FiltersGroup, limit: number): string[] => {
  const shown = getSlicedFiltersGroup(filtersGroup, limit);
  return filtersGroup.filters.flatMap((filter) => {
    if (filter.type !== FILTER_TYPE.MULTI_SELECT) return [];
    const total = (filter.data as FilterDataMultiSelect).options.length;
    const shownFilter = shown.filters.find((f) => f.name === filter.name);
    const kept = (shownFilter?.data as FilterDataMultiSelect | undefined)?.options.length ?? 0;
    return Array.from({ length: total - kept }, (_, i) => optionCheckboxId(filter.name, kept + i));
  });
};

const ShowMoreContainer: React.FC<ShowMoreControlProps> = ({
  hasLimit,
  limit = 10,
  onShowMore,
  filtersGroup,
  children,
}) => {
  const { t } = useTranslation();
  const buttonId = React.useId();

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const hasActiveFilter = filtersGroup.filters.some(
      (filter) =>
        filter.type === FILTER_TYPE.MULTI_SELECT &&
        (filter.data as FilterDataMultiSelect).options.some((o) => o.selected)
    );
    const totalOptions = filtersGroup.filters.reduce(
      (total, filter) => total + ((filter.data as FilterDataMultiSelect)?.options?.length ?? 0),
      0
    );
    return !!filtersGroup.showMore && hasLimit && !hasActiveFilter && totalOptions > limit;
  });

  const visibleFiltersGroup = useMemo(
    () => (isCollapsed ? getSlicedFiltersGroup(filtersGroup, limit) : filtersGroup),
    [isCollapsed, filtersGroup, limit]
  );

  const revealTargets = useRef<string[] | null>(null);

  useEffect(() => {
    const ids = revealTargets.current;
    if (isCollapsed || !ids) return;
    revealTargets.current = null;
    ids
      .map((id) => document.getElementById(id))
      .find((el) => el && !el.matches(':disabled'))
      ?.focus();
  }, [isCollapsed]);

  const handleShowMore = () => {
    // The heading catches focus when every revealed option is disabled, as Show more has unmounted
    revealTargets.current = [...revealedOptionIds(filtersGroup, limit), filterGroupTitleId(filtersGroup.id)];
    setIsCollapsed(false);
    onShowMore?.();
  };

  return (
    <>
      {children(visibleFiltersGroup)}
      {isCollapsed && (
        <ShowMoreButton
          id={buttonId}
          aria-labelledby={`${buttonId} ${filterGroupTitleId(filtersGroup.id)}`}
          onClick={handleShowMore}
        >
          {t('vacation.oss.cards.view_more')}
        </ShowMoreButton>
      )}
    </>
  );
};

export default ShowMoreContainer;
